/**
 * Connection-quality classification (007-connection-quality).
 *
 * Pure, framework-free helpers that turn WebRTC stats samples into a small
 * `QualityLevel`. No React, no zustand, no DOM, no timers — so the whole
 * classifier is unit-testable without a live connection (see
 * tests/unit/connectionQuality.test.ts).
 *
 * Thresholds and cadence come from specs/007-connection-quality/research.md
 * (Decision 3 + Decision 4).
 */

export type QualityLevel = 'unknown' | 'poor' | 'good' | 'excellent'

/** Total bars in the gauge. Filled-bar count is the greyscale-safe level cue (FR-008). */
export const QUALITY_TOTAL_BARS = 3

/** Filled bars (of QUALITY_TOTAL_BARS) per level — shape cue independent of colour. */
export const QUALITY_BARS: Record<QualityLevel, number> = {
  unknown: 0,
  poor: 1,
  good: 2,
  excellent: 3,
}

/** i18n key (under the `tile.signal` namespace) for each level's label. */
export const QUALITY_I18N_KEY: Record<QualityLevel, string> = {
  unknown: 'tile.signal.unknown',
  poor: 'tile.signal.poor',
  good: 'tile.signal.good',
  excellent: 'tile.signal.excellent',
}

// ── Thresholds (research.md Decision 3) ────────────────────────────────────
/** Loss ratio below this (and low jitter) → excellent. */
export const LOSS_EXCELLENT = 0.02
/** Loss ratio below this → good; at/above → poor. */
export const LOSS_GOOD = 0.1
/** Jitter (seconds) must be below this for `excellent`. */
export const JITTER_EXCELLENT_SEC = 0.03
/** Outbound RTT (seconds) ceiling for `excellent`. */
export const RTT_EXCELLENT_SEC = 0.3
/** Outbound RTT (seconds) ceiling for `good`. */
export const RTT_GOOD_SEC = 0.6
/** EWMA smoothing factor for the loss ratio (research.md Decision 4). */
export const EMA_ALPHA = 0.5

/**
 * Transient per-stream stats snapshot. Inbound fields come from an `inbound-rtp`
 * report (remote tiles); outbound fields from a `remote-inbound-rtp` report
 * (local tile). All optional — a missing field yields `unknown`, never a throw.
 */
export interface QualitySample {
  timestampMs: number
  // Inbound (remote tiles) — cumulative counters, classified via deltas.
  packetsReceived?: number | undefined
  packetsLost?: number | undefined
  jitterSec?: number | undefined
  // Outbound (local tile) — already-ratio'd values, classified directly.
  fractionLost?: number | undefined
  roundTripTimeSec?: number | undefined
  // Smoothed loss ratio carried tick-to-tick (inbound only).
  emaLossRatio?: number | undefined
}

/**
 * Classify a remote downlink from successive `inbound-rtp` samples.
 *
 * Returns the level plus the updated EWMA loss ratio to carry into the next
 * tick's sample. Without a usable previous sample (first tick, missing counter,
 * or a counter reset) the result is `unknown`. When no packets flowed this
 * interval, an *active* video track is treated as stalled/lost → `poor`
 * (FR-010); an idle/cam-off track stays `unknown`.
 */
export function classifyInbound(
  prev: QualitySample | undefined,
  cur: QualitySample,
  opts: { videoActive?: boolean } = {},
): { level: QualityLevel; emaLossRatio: number | undefined } {
  if (
    prev === undefined ||
    cur.packetsReceived === undefined ||
    prev.packetsReceived === undefined ||
    cur.packetsLost === undefined ||
    prev.packetsLost === undefined
  ) {
    return { level: 'unknown', emaLossRatio: undefined }
  }

  const deltaReceived = cur.packetsReceived - prev.packetsReceived
  const deltaLost = cur.packetsLost - prev.packetsLost

  // Counter reset or nonsensical delta → no signal this tick (keep prior EWMA).
  if (deltaReceived < 0 || deltaLost < 0) {
    return { level: 'unknown', emaLossRatio: prev.emaLossRatio }
  }

  const total = deltaReceived + deltaLost
  if (total === 0) {
    return {
      level: opts.videoActive ? 'poor' : 'unknown',
      emaLossRatio: prev.emaLossRatio,
    }
  }

  const instantLoss = deltaLost / total
  const ema =
    prev.emaLossRatio === undefined
      ? instantLoss
      : EMA_ALPHA * instantLoss + (1 - EMA_ALPHA) * prev.emaLossRatio

  const jitter = cur.jitterSec ?? 0
  let level: QualityLevel
  if (ema < LOSS_EXCELLENT && jitter < JITTER_EXCELLENT_SEC) level = 'excellent'
  else if (ema < LOSS_GOOD) level = 'good'
  else level = 'poor'

  return { level, emaLossRatio: ema }
}

/**
 * Classify the local uplink from a single `remote-inbound-rtp` sample
 * (the SFU's RTCP receiver report). `fractionLost` is already a ratio, so no
 * delta is needed. Missing `fractionLost` → `unknown`.
 */
export function classifyOutbound(cur: QualitySample): QualityLevel {
  if (cur.fractionLost === undefined) return 'unknown'
  const rtt = cur.roundTripTimeSec ?? 0
  if (cur.fractionLost < LOSS_EXCELLENT && rtt < RTT_EXCELLENT_SEC) return 'excellent'
  if (cur.fractionLost < LOSS_GOOD && rtt < RTT_GOOD_SEC) return 'good'
  return 'poor'
}

/**
 * Aggregate a participant's per-track levels into one. Video is what the user
 * perceives first, so a *known* video level wins; otherwise fall back to a
 * known audio level (covers camera-off tiles); otherwise `unknown`.
 */
export function aggregateLevels(
  video: QualityLevel | null,
  audio: QualityLevel | null,
): QualityLevel {
  if (video && video !== 'unknown') return video
  if (audio && audio !== 'unknown') return audio
  return video ?? audio ?? 'unknown'
}
